import { observable, computed, action } from 'mobx'
import { has } from 'lodash'

import BaseListItem, {
  IBaseListItemUpdateOptions,
} from 'stores/base/BaseListItem'
import ActionStore from 'stores/action/ActionStore'
import ScriptStore from 'stores/script/ScriptStore'
import Script from 'stores/script/Script'
import ProjectStore from 'stores/project/ProjectStore'
import { ISessionSchema } from 'stores/persistence/types'
import SessionSelection from './SessionSelection'

import createError from 'utils/createError'

export type ISessionType = 'script' | 'model-list'
export type ISessionCreatePayload = {
  id: string
  type: ISessionType
  scriptId: string | null
  lastSavedHash: string | null
}

export const MODEL_LIST_SESSION_ID = 'model-list-session'

class Session extends BaseListItem {
  @observable id: string
  @observable type: ISessionType
  @observable scriptId: string | null

  @observable lastSavedHash: string
  selection: SessionSelection

  constructor(session: ISessionCreatePayload) {
    super(session)

    this.id = session.id
    this.type = session.type
    this.scriptId = session.scriptId ?? null

    this.lastSavedHash = session.lastSavedHash ?? this.hash
    this.selection = new SessionSelection({
      table: {
        sessionId: this.id,
        selectedRecordIds: [],
      },
      tree: {
        sessionId: this.id,
        isEditing: false,
      },
    })
  }

  @computed
  get script(): Script {
    if (!this.isScript) {
      throw createError(
        'Session.script',
        'Invalid `get` call to script: Session is not a `script` type',
        { type: this.type, scriptId: this.scriptId }
      )
    }

    const script = ScriptStore.get(this.scriptId)
    if (!script) {
      throw createError('Session.script', 'Invalid scriptId in session', {
        type: this.type,
        scriptId: this.scriptId,
      })
    }
    return script
  }

  @computed
  get name(): string {
    if (this.isScript) {
      return this.script.model.name
    }

    return 'Session Name'
  }

  @computed
  get isScript() {
    return this.type === 'script'
  }

  @computed
  get isModelList() {
    return this.type === 'model-list'
  }

  @computed
  get isDirty(): boolean {
    if (this.isScript) {
      // Frozen scripts are never dirty
      if (this.script.frozen) return false

      // Unsaved scripts are always dirty
      if (this.script.name === null) {
        return true
      }

      // Sessions with at least ine action are always dirty
      if (
        Object.values(ActionStore.values).filter(a => a.sessionId === this.id)
      ) {
        return true
      }

      return this.script.name !== null && this.lastSavedHash !== this.hash
    }

    return this.lastSavedHash !== this.hash
  }

  @computed
  get hash(): string {
    if (this.isScript) {
      return this.script.hash
    }

    return ''
  }

  @action
  createUncommittedRecord = () => {
    if (!this.isScript) {
      return
    }

    // Create a `create` action
    const createAction = ActionStore.add({
      type: 'create',
      recordId: null,
      sessionId: this.id,
      value: {
        modelId: this.script.modelId,
      },
    })

    // And add another one to populate the new record's fields with default values
    // We cannot do this in ActionStore.add directly because it will mess with IDB restores after a refresh.
    // Specifically, it will add a new update action every time the create action is restored (because it does not know that the update action will eventually be restored)
    ActionStore.add({
      type: 'update',
      recordId: createAction.recordId,
      sessionId: this.id,
      value: this.script.model.fields.reduce((v, field) => {
        if (field.isId) {
          // For ID fields, there's no way to guarantee a valid value, so don't attempt to generate one
          v[field.name] = undefined
        } else if (field.default !== undefined) {
          // If there is a default and it is a function, set it to undefined, because it can only be evaluated at (query) runtime
          if (field.isFunctionDefault) {
            v[field.name] = undefined
          } else {
            v[field.name] = field.default
          }
        } else if (
          (field.isScalar || field.isEnum) &&
          !field.isPartOfRelation
        ) {
          // If this is a scalar or enum that isn't part of a relation, pre-populate with the lowest valid value
          v[field.name] = field.lowestValidValue
        } else if (field.isRelation && field.isList) {
          // If this is a relation list, also pre-populate with the lowest valid value (AKA `[]`)
          v[field.name] = field.lowestValidValue
        } else {
          // In all other cases, set the value to undefined
          v[field.name] = undefined
        }
        return v
      }, {} as any),
    })
  }

  @action
  forceSave() {
    if (!this.isDirty) {
      return
    }

    // Saving a session means updating its `lastSavedHash`
    this.update({ lastSavedHash: this.hash })
  }

  @action
  discardChanges() {
    if (this.isScript) {
      try {
        const {
          code,
          modelId,
          where,
          fieldIds,
          sortFieldId,
          sortOrder,
        } = JSON.parse(this.lastSavedHash) // The last saved hash is guaranteed to be a script hash

        this.script.update({
          code,
          modelId,
          where,
          fieldIds,
          sort: {
            fieldId: sortFieldId,
            order: sortOrder,
          },
        })
      } catch (e) {
        console.log('Could not restore session', e)
      }
    }

    // Handle discards for other types of sessions
  }

  @action
  update = (
    session: Partial<ISessionCreatePayload>,
    options: IBaseListItemUpdateOptions = {}
  ) => {
    has(session, 'lastSavedHash') &&
      (this.lastSavedHash = session.lastSavedHash!)

    super.update(session, options)
  }

  serialize = (): ISessionSchema => {
    return {
      projectId: ProjectStore.activeProjectId!,

      id: this.id,
      type: this.type,
      lastSavedHash: this.lastSavedHash,
      scriptId: this.scriptId,
    }
  }
}

export default Session
